import Head from 'next/head';
import { NextPage } from 'next';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import type { AuthUserApiResponse } from 'src/common/types/api/v4/auth/user';
import type { HomepageResults } from 'src/common/types/api/v4/search/v1/homepage';
import type { HomepageWorldwideLocationsData } from 'src/homepage/components/HomepageWorldwideLocations/types';
import handleApiErrors from 'src/common/utils/handleApiErrors';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import serverGetUser from 'src/common/utils/serverGetUser';
import useUpdateDataToMatchCurrency from 'src/common/hooks/useUpdateDataToMatchCurrency';
import GdprCompliance from 'src/common/components/GdprCompliance';
import Layout from 'src/common/components/Layout';
import ReviewAndAppLinks from 'src/common/components/ReviewAndAppLinks';
import DescriptionMetaTag from 'src/common/components/MetaTags/DescriptionMetaTag';
import Footer from 'src/common/components/Footer';
import FooterLinks from 'src/common/components/FooterLinks';
import OpenGraphMetaTags from 'src/common/components/MetaTags/OpenGraphMetaTags';
import OrganizationSchema from 'src/common/components/MetaTags/OrganizationSchema';
import { COMPACT_MODE_COOKIE_NAME } from 'src/common/constants';
import serverGetHomepageData from 'src/homepage/utils/serverGetHomepageData';
import { type HomepageFields } from 'src/homepage/utils/HomepageFields';
import serverGetWorldwideLocationsData from 'src/homepage/utils/serverGetWorldwideLocationsData';
import MainContentPlaceholder from 'src/common/components/MainContentPlaceholder';
import HomepageWorldwideLocations from 'src/homepage/components/HomepageWorldwideLocations';
import SubFooter from 'src/common/components/SubFooter';
import { getRandomSticker } from 'src/common/components/Sticker';
import { StickerCategories, StickerConfig } from 'src/common/components/Sticker/stickerMap';
import SearchInputProvider from 'src/common/components/Search/SearchInputProvider';
import SearchHeroVisibilityProvider from 'src/common/components/Search/SearchHeroVisibilityProvider';
import MobileWebViewHeader from 'src/common/components/MobileWebViewHeader';
import getCookie from 'src/common/utils/getCookie';
import hasFeatureFlagEnabled from 'src/common/utils/hasFeatureFlagEnabled';
import HeaderSticky from 'src/common/components/HeaderSticky';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';

const HomePageContent = dynamic(
  () => import('src/homepage/components/Root'),
  { loading: () => <MainContentPlaceholder /> },
);

type HomePageProps = {
  homepage: HomepageResults;
  user: Partial<AuthUserApiResponse>;
  headerStickers?: Record<StickerCategories, StickerConfig> | null;
  worldwideLocations: HomepageWorldwideLocationsData;
};

const HomePage: NextPage<HomePageProps> = ({
  homepage,
  user,
  headerStickers,
  worldwideLocations,
}) => {
  const [t] = useTranslation('home-page');
  const title = t('title');
  const description = t('meta-description');

  const currencyCode = homepage?.recommended_listings?.[0]?.request_currency?.code;
  useUpdateDataToMatchCurrency(currencyCode);

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>

      <OrganizationSchema />

      {/**
       * The reason why this is outside <Head> is because
       * NextJS behaves a little bit funky when rendering
       * custom components inside <Head> ...
       * (see https://github.com/vercel/next.js/issues/9126)
       */}
      <DescriptionMetaTag content={description} />
      <OpenGraphMetaTags
        canonicalPath="/"
        title={title}
        description={description}
      />

      <Layout overscrollColor="ivory">
        {/* Only show the Cookie banner if the user is logged in */}
        {!user?.id && <GdprCompliance />}
        <SearchInputProvider>
          <SearchHeroVisibilityProvider>
            <MobileWebViewHeader />
            <HeaderSticky
              variant="ivory"
              showBottomBorderOnScroll
            />
            <HomePageContent homepage={homepage} headerStickers={headerStickers} />
          </SearchHeroVisibilityProvider>
        </SearchInputProvider>
        <Footer>
          <HomepageWorldwideLocations
            data={worldwideLocations}
          />
          <FooterLinks />
          <ReviewAndAppLinks />
          <SubFooter />
        </Footer>
      </Layout>
    </>
  );
};

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<HomePageProps>(async (context) => {
    let user: Partial<AuthUserApiResponse> = {};
    let homepage: any = {};
    const translations = loadTranslations(
      context.locale,
      [
        'common',
        'home-page',
        'navbar',
        'destination-navigation',
        'worldwide-locations',
        'search-and-filters',
        'boat-categories',
        'boat-type-cards',
        'footer',
        'static-reviews',
      ],
    );

    const isMobileNativeWebView = getCookie(context, COMPACT_MODE_COOKIE_NAME) || hasFeatureFlagEnabled(context, 'mobile-webview');
    const fields: HomepageFields[] | undefined = isMobileNativeWebView ? ['listing_types'] : undefined;

    const promises = [
      serverGetUser(context).then((data) => {
        user = data;
      }),
      serverGetHomepageData(context, fields).then((data) => {
        homepage = data;
      }),
    ];

    const worldwideLocations = serverGetWorldwideLocationsData(context);

    const headerStickers: Record<StickerCategories, StickerConfig> | null = {
      boats: getRandomSticker('boats'),
      actions: getRandomSticker('actions'),
      things: getRandomSticker('things'),
    };

    await Promise.all(promises);

    return {
      props: {
        homepage,
        user,
        headerStickers,
        worldwideLocations: await worldwideLocations,
        ...(await translations),
      },
    };
  }),
);

export default HomePage;
